<script setup lang="ts">
    import { ref, watch } from "vue";
    import ChevronBlueIcon from "@/assets/dropdown-arrow-blue.svg";
    import Button from "@/components/Button.vue";
    import KAutocomplete from "@/components/forms/inputs/Autocomplete/KAutocomplete.vue";
    import KRepeater from "@/components/forms/groups/Repeater/KRepeater.vue";
    import type { Option } from "@/components/forms/inputs/Autocomplete/types";
    import KNumber from "@/components/forms/inputs/Number/KNumber.vue";
    import KText from "@/components/forms/inputs/Text/KText.vue";
    import KDateTimepicker from "@/components/forms/inputs/DateTimepicker/KDateTimepicker.vue";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import TidInput from "../../TidInput.vue";
    import { calculateNews2Bedomning, NEWS2_REQUIRED_FIELDS, type News2Entry } from "./news2";
    import ButtonLink from "@/components/ButtonLink.vue";

    withDefaults(
        defineProps<{
            name?: string;
            readonly?: boolean;
            updatedAt?: string | null;
        }>(),
        {
            name: "news2",
            readonly: false,
            updatedAt: null,
        }
    );

    const ACVPU_OPTIONS: Option[] = [
        { value: "", label: "Välj medvetandegrad" },
        { value: "A", label: "A – Alert" },
        { value: "C", label: "C – Confusion" },
        { value: "V", label: "V – Verbal" },
        { value: "P", label: "P – Pain" },
        { value: "U", label: "U – Unresponsive" },
    ];

    const OXYGEN_SUPPLY_OPTIONS: Option[] = [
        { value: "Ja", label: "Ja" },
        { value: "Nej", label: "Nej" },
    ];

    const ACVPU_EXPLANATIONS = [
        {
            code: "A",
            title: "A = Alert",
            description:
                "Innebär att patienten är vaken, öppnar spontant ögonen och reagerar på tilltal. Patienten har även oftast motorisk funktion och kan på uppmaning röra sina extremiteter. Vid bedömning nattetid när patienten sover får hänsyn tas till att mer stimuli kan behövas för att väcka patienten. En patient som sover och behöver väckas med höjd röst eller med handpåläggning kan bedömas som alert om denne efter uppvaknandet ter sig alert.",
        },
        {
            code: "C",
            title: "C = Confusion",
            description:
                "Innebär att patienten har en nytillkommen eller förvärrad förvirring som även kan benämnas konfusion. Patienten är ej adekvat, kan ha svårt att redogöra för tid, rum och person. Ibland är patienten motoriskt orolig och drar i slangar eller kläder. Om patientens tidigare status är okänt ska förvirringen räknas som nytillkommen tills motsatsen är bevisad.",
        },
        {
            code: "V",
            title: "V = Verbal",
            description:
                "Innebär en medvetandesänkt patient som reagerar på tilltal. Patienten reagerar med ögonöppning, tal eller rörelse på verbal stimulering. Även läten från munnen som inte kan uppfattas som tal innebär en reaktion på tilltal. Använd hög röst när du tilltalar patienten.",
        },
        {
            code: "P",
            title: "P = Pain",
            description:
                "Innebär en medvetandesänkt patient som reagerar på smärtstimulering men inte på tilltal. Smärtstimulera patienten både perifert på nagelbädden och centralt i käkvinkeln. Patienten reagerar med ögonöppning, tal eller rörelse på smärtstimuleringen. Även läten från munnen som inte kan uppfattas som tal innebär en reaktion på smärtstimulering. Smärtstimulering innebär ofta att patienten drar undan den kroppsdel som smärtstimuleras",
        },
        {
            code: "U",
            title: "U = Unresponsive",
            description:
                "Innebär att patienten är medvetandesänkt och inte reagerar på tilltal eller smärtstimulering. ",
        },
    ] as const;

    const showAcvpuModal = ref(false);
    const repeaterRef = ref();
    const rowsWithErrors = ref<Set<number>>(new Set());

    const fieldError = (index: number, value: unknown): string[] | undefined => {
        if (!rowsWithErrors.value.has(index)) return undefined;
        if (value !== null && value !== "" && value !== undefined) return undefined;
        return ["Fältet är obligatoriskt"];
    };

    const expandedRows = ref<Record<number, boolean>>({ 0: true });

    const isExpanded = (index: number) => expandedRows.value[index] ?? false;

    const toggleExpanded = (index: number) => {
        expandedRows.value = { ...expandedRows.value, [index]: !isExpanded(index) };
    };

    const handleRowAdded = ({ index }: { index: number }) => {
        expandedRows.value = {
            ...Object.fromEntries(Object.keys(expandedRows.value).map((k) => [Number(k), false])),
            [index]: true,
        };
    };

    const hasOnlyAutoExpandedFirstRow = () =>
        Object.keys(expandedRows.value).length === 1 && expandedRows.value[0] === true;

    watch(
        () => repeaterRef.value?.rowCount,
        (newCount: number, oldCount: number) => {
            if (!hasOnlyAutoExpandedFirstRow()) return;
            if (oldCount === 1 && newCount > 1) {
                expandedRows.value = Object.fromEntries(Array.from({ length: newCount }, (_, i) => [i, false]));
            }
        }
    );
    const calculateNews2 = async ({ model, index }: { model?: News2Entry[]; index: number }) => {
        const currentBedomning = model?.[index];

        if (!currentBedomning) {
            return;
        }

        const hasMissing = NEWS2_REQUIRED_FIELDS.some((field) => {
            const val = currentBedomning[field];
            return val === null || val === "" || val === undefined;
        });

        if (hasMissing) {
            rowsWithErrors.value = new Set([...rowsWithErrors.value, index]);
        } else {
            rowsWithErrors.value = new Set([...rowsWithErrors.value].filter((i) => i !== index));
        }

        // Validerar övriga regler (min/max/decimals) på raden. Required-markeringen hanteras separat ovan.
        const isCurrentRowValid = await repeaterRef.value?.validateRow?.({ index });

        if (hasMissing || isCurrentRowValid === false) {
            return;
        }

        const { news2_score, risk_kategori } = calculateNews2Bedomning(currentBedomning);
        currentBedomning.news2_score = news2_score;
        currentBedomning.risk_kategori = risk_kategori;
    };
</script>

<template>
    <section class="news2-form">
        <header class="news2-form__header">
            <h3 class="news2-form__heading">NEWS2 (National Early Warning Score 2)</h3>
        </header>

        <KRepeater
            ref="repeaterRef"
            :name="name"
            add-label="Lägg till NEWS2-bedömning"
            remove-label="Ta bort NEWS2-bedömning"
            :readonly="readonly"
            class="news2-repeater"
            @add="handleRowAdded"
        >
            <template #default="{ index, model }">
                <div class="news2-entry span-all">
                    <button type="button" class="news2-entry__header" @click="toggleExpanded(index)">
                        <span class="news2-entry__title">NEWS2-bedömning {{ index + 1 }}</span>
                        <img
                            :src="ChevronBlueIcon"
                            :alt="`Växla NEWS2-bedömning ${index + 1}`"
                            class="news2-entry__chevron"
                            :class="{ rotate: isExpanded(index) }"
                        />
                    </button>

                    <div class="news2-entry__timestamp">
                        <TidInput
                            name="tidpunkt_news2_bedomning"
                            label="Tidpunkt för NEWS2-bedömning"
                            :readonly="readonly"
                        />
                    </div>

                    <div v-if="!isExpanded(index)" class="news2-entry__divider"></div>

                    <div v-show="isExpanded(index)" class="news2-entry__fields">
                        <KNumber
                            name="respiratoryRate"
                            label="Andningsfrekvens (per minut)"
                            :value="model?.[index]?.respiratoryRate ?? ''"
                            :minValue="5"
                            :maxValue="50"
                            :showAsRequired="true"
                            :errors="fieldError(index, model?.[index]?.respiratoryRate)"
                            :readonly="readonly"
                        />

                        <KNumber
                            name="oxygenSaturation"
                            label="Syremättnad (%)"
                            :value="model?.[index]?.oxygenSaturation ?? ''"
                            :minValue="0"
                            :maxValue="100"
                            :showAsRequired="true"
                            :errors="fieldError(index, model?.[index]?.oxygenSaturation)"
                            :readonly="readonly"
                        />

                        <KNumber
                            name="oxygenSaturation2"
                            label="Syremättnad 2 (%)"
                            :value="model?.[index]?.oxygenSaturation2 ?? ''"
                            :minValue="0"
                            :maxValue="100"
                            :readonly="readonly"
                        />

                        <KAutocomplete
                            name="oxygenSupply"
                            label="Tillförd syrgas"
                            :options="OXYGEN_SUPPLY_OPTIONS"
                            :value="model?.[index]?.oxygenSupply ?? ''"
                            :showAsRequired="true"
                            :errors="fieldError(index, model?.[index]?.oxygenSupply)"
                            :readonly="readonly"
                        />

                        <KNumber
                            name="systolicBP"
                            label="Blodtryck (systoliskt)"
                            :value="model?.[index]?.systolicBP ?? ''"
                            :minValue="50"
                            :maxValue="250"
                            :showAsRequired="true"
                            :errors="fieldError(index, model?.[index]?.systolicBP)"
                            :readonly="readonly"
                        />

                        <KNumber
                            name="diastolicBP"
                            label="Blodtryck (diastoliskt)"
                            :value="model?.[index]?.diastolicBP ?? ''"
                            :minValue="0"
                            :readonly="readonly"
                        />

                        <KNumber
                            name="pulse"
                            label="Puls (slag per minut)"
                            :value="model?.[index]?.pulse ?? ''"
                            :minValue="20"
                            :maxValue="200"
                            :showAsRequired="true"
                            :errors="fieldError(index, model?.[index]?.pulse)"
                            :readonly="readonly"
                        />

                        <div class="news2-entry__consciousness-wrapper">
                            <div class="news2-entry__consciousness">
                                <KAutocomplete
                                    name="consciousness"
                                    label="Medvetandegrad (ACVPU)"
                                    :options="ACVPU_OPTIONS"
                                    :value="model?.[index]?.consciousness ?? ''"
                                    :showAsRequired="true"
                                    :errors="fieldError(index, model?.[index]?.consciousness)"
                                    :readonly="readonly"
                                />
                                <ButtonLink
                                    class="news2-entry__help"
                                    @click.prevent="showAcvpuModal = true"
                                    type="button"
                                >
                                    Förklaring
                                </ButtonLink>
                            </div>

                            <KNumber
                                name="temperature"
                                label="Temperatur (grader)"
                                :value="model?.[index]?.temperature ?? ''"
                                :minValue="10"
                                :maxValue="50"
                                validation="decimals:1"
                                :showAsRequired="true"
                                :errors="fieldError(index, model?.[index]?.temperature)"
                                :readonly="readonly"
                            />
                        </div>

                        <div class="news2-entry__actions span-all">
                            <Button
                                variant="contour"
                                size="secondary"
                                type="button"
                                :disabled="readonly"
                                @click="calculateNews2({ model, index })"
                            >
                                Beräkna NEWS2
                            </Button>
                        </div>
                    </div>

                    <div class="news2-entry__fields">
                        <KText
                            name="news2_score"
                            label="NEWS2 totalpoäng"
                            :value="model?.[index]?.news2_score ?? ''"
                            placeholder="-"
                        />

                        <KText
                            name="risk_kategori"
                            label="NEWS2 riskkategori"
                            :value="model?.[index]?.risk_kategori ?? ''"
                            placeholder="-"
                            :class="{
                                'news2-entry__risk-field--low': model?.[index]?.risk_kategori === 'Låg',
                                'news2-entry__risk-field--low-medium': model?.[index]?.risk_kategori === 'Låg/Medium',
                                'news2-entry__risk-field--medium': model?.[index]?.risk_kategori === 'Medium',
                                'news2-entry__risk-field--high': model?.[index]?.risk_kategori === 'Hög',
                            }"
                        />

                        <KDateTimepicker
                            name="fornyad_news2_bedomning"
                            type="time"
                            convertTo="time"
                            label="Förnyad bedömning"
                            :showNowButton="true"
                            :readonly="readonly"
                        />

                        <KText
                            name="avsteg_fran_fornyad_news2_bedomning"
                            label="Avsteg från förnyad bedömning"
                            type="textarea"
                            :value="model?.[index]?.avsteg_fran_fornyad_news2_bedomning ?? ''"
                            :readonly="readonly"
                        />
                    </div>
                </div>
            </template>
        </KRepeater>

        <!-- ACVPU Förklaring modal -->
        <ModalContent
            v-if="showAcvpuModal"
            title="Medvetandegrad – ACVPU förklaring"
            :show-close-button="true"
            :show-header="true"
            width="wide"
            close-button-variant="large"
            @close="showAcvpuModal = false"
        >
            <div class="acvpu-modal">
                <dl class="acvpu-modal__list">
                    <div v-for="item in ACVPU_EXPLANATIONS" :key="item.code" class="acvpu-modal__item">
                        <dt class="acvpu-modal__term">{{ item.title }}</dt>
                        <dd class="acvpu-modal__description">{{ item.description }}</dd>
                    </div>
                </dl>
            </div>

            <span class="acvpu-modal__item">Senaste uppdaterad: 2026-06-03</span>

            <ButtonGroup align="left">
                <Button variant="standard" @click="showAcvpuModal = false">Stäng</Button>
            </ButtonGroup>
        </ModalContent>
    </section>
</template>

<style scoped lang="scss">
    @import "@/style/_variables";

    .news2-form {
        padding: 0 0 1rem;

        &__header {
            padding: 1rem 1rem 0.5rem;
        }

        &__heading {
            margin: 0;
            font-size: 1rem;
            font-weight: 600;
            color: $color-active-dark;
        }

        &__updated-at {
            display: block;
            margin-top: 0.25rem;
            font-size: 0.8rem;
            color: $color-text-grey;
        }
    }

    .news2-repeater {
        margin: 0;
        ::v-deep .remove-button {
            display: none;
        }
    }

    .news2-entry {
        padding: 16px;
        margin-bottom: 16px;
        background-color: $color-grey-very-light;
        border: 1px solid $color-grey-light;
        border-radius: $border-radius-soft;

        &__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            margin: 0 0 8px;
            padding: 0;
            background: transparent;
            border: 0;
            text-align: left;
            cursor: pointer;
        }

        &__title {
            margin: 0;
            font-size: 0.95rem;
            font-weight: 600;
            color: $color-active-dark;
        }

        &__chevron {
            width: 18px;
            height: 18px;
            transition: transform 0.2s ease;

            &.rotate {
                transform: rotate(180deg);
            }
        }

        &__fields {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 16px;
            align-items: start;
            .news2-entry__consciousness-wrapper {
                .news2-entry__consciousness {
                    .news2-entry__help {
                        background-color: transparent;
                        border: none;
                        cursor: pointer;
                        background-image: url("@/assets/link_internal.svg");
                        background-position: left;
                        background-size: 13px 13px;
                        height: auto;
                        padding-left: 18px;
                        position: relative;
                    }
                }
            }
            @media (max-width: 900px) {
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            @media (max-width: 600px) {
                grid-template-columns: 1fr;
            }
        }

        &__timestamp {
            grid-column: 1 / -1;

            ::v-deep(.formulate-input-wrapper) {
                width: $column-width;
                max-width: 100%;
            }
        }

        &__consciousness-wrapper {
            grid-column: 1 / -1;
            display: flex;
            gap: 18px;

            ::v-deep(.formulate-input-wrapper) {
                width: $column-width;
                max-width: 100%;
            }
        }

        &__consciousness {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }

        &__help {
            margin-top: 2px;
            min-height: auto;
            padding: 0;
        }

        &__actions {
            display: flex;
            justify-content: flex-start;
        }

        &__divider {
            width: 100%;
            border-top: 1px solid $color-grey;
            margin: 16px 0;
        }

        &__risk-field {
            &--low {
                ::v-deep input {
                    background-color: $color-approval;
                }
            }

            &--low-medium {
                ::v-deep input {
                    background-color: #ffefb8;
                }
            }

            &--medium {
                ::v-deep input {
                    background-color: #ffd3c7;
                }
            }

            &--high {
                ::v-deep input {
                    background-color: #ffb2a0;
                }
            }
        }
    }

    .span-all {
        grid-column: 1 / -1;
    }
</style>
